const PATHS = {
  HOME: "/",
  NOT_FOUND: "/404",
  LOGIN: "/login",
  LOGOUT: "/logout",
  VERIFICATION: "/verify",
  FORGET_PASSWORD: "/forget-password",
  RESET_PASSWORD: "/reset-password",
  REGISTER: "/register",
  REGISTER_ADMIN: "/register/admin",
  DASHBOARD: "/dashboard",
  SCHEDULE: "/schedule",
  LIST: "/list",
  CUSTOMER: "/customer",
  CUSTOMER_PACKAGE: "/customer/package",
  STAFF: "/staff",
  // Karyawan sidebar group: one list per employee type (like Produk's Paket /
  // Membership). Static segments win over the /staff/:id detail route.
  KARYAWAN_INSTRUCTORS: "/staff/instructors",
  KARYAWAN_STAFF: "/staff/staff",
  PACKAGE: "/package",
  // Create page for a credit package (?class_id= preselects a class).
  PACKAGE_NEW: "/package/new",
  // Membership products page (card grid); /membership/:productId is the
  // membership product detail.
  MEMBERSHIP: "/membership",
  // Create page for a membership (?class_id= preselects a class). The same
  // page as the detail, under /membership so the sidebar keeps Membership
  // highlighted.
  MEMBERSHIP_NEW: "/membership/new",
  USER_PROFILE: "/profile/user",
  BUSINESS_PROFILE: "/profile/business",
  // Manager-only: manage this business's Admin/Manager accounts from their
  // own dashboard (the SystemAdmin equivalent is the "admins" tab on
  // BUSINESS/:businessId).
  ACCOUNTS: "/accounts",
  // Manager-only payroll section (parallel to ACCOUNTS): frequency setting,
  // commission-rate config, and recurring items live on PAYROLL. PAYROLL_RUN
  // is used as `${PAYROLL_RUN}/:runId` — one page across a run's whole
  // lifecycle (draft/reopened renders editable, finalized renders read-only),
  // not split into separate draft/detail routes.
  PAYROLL: "/payroll",
  // Class-first commission page: one class, one chosen structure, a roster
  // of per-instructor rate values. Used as `${PAYROLL_CLASS_RATE}/:classTypeId`.
  PAYROLL_CLASS_RATE: "/payroll/rates",
  PAYROLL_RUN_NEW: "/payroll/run/new",
  PAYROLL_RUN: "/payroll/run",
  PAYROLL_HISTORY: "/payroll/history",
  // Business-wide booking policy (cancellation/waitlist/schedule visibility/
  // schedule conflict/auto-complete).
  // Its own top-level tab, not nested under Kelas. The policy applies
  // business-wide, not per class type.
  //
  // /settings itself is a launcher: every entry below is one tile on it.
  SETTINGS: "/settings",
  // Kebijakan Booking & Jadwal: the six booking and product rules on one page
  // (2026-10-07). The six per-rule paths below only redirect to its sections.
  SETTINGS_POLICIES: "/settings/policies",
  SETTINGS_CANCELLATION: "/settings/cancellation",
  SETTINGS_WAITLIST: "/settings/waitlist",
  SETTINGS_VISIBILITY: "/settings/visibility",
  SETTINGS_SCHEDULE_CONFLICT: "/settings/schedule-conflict",
  SETTINGS_AUTO_COMPLETE: "/settings/auto-complete",
  SETTINGS_VALIDITY_START: "/settings/validity-start",
  // The studio's manual payment-method list (used on package purchase/edit
  // and printed on Invoice/Receipt PDFs).
  SETTINGS_PAYMENT_METHODS: "/settings/payment-methods",
  // The studio's WhatsApp contact for the client app: on/off plus the number
  // behind every WhatsApp button there. Sits beside payment methods because
  // that chat is how a client buys a product or claims a discount reward.
  SETTINGS_WHATSAPP: "/settings/whatsapp",
  // Sales tax on/off + mode + rate + label, applied to purchase Invoice/
  // Receipt PDFs and the Monthly Statement of Account.
  SETTINGS_TAX: "/settings/tax",
  // Laporan Rincian Akun Bulanan (Monthly Statement of Account) — the
  // studio's own sales + tax rollup. Lives in Settings (not Laporan) since
  // it's a compliance/bookkeeping document, not a performance report; pairs
  // with Pengaturan Pajak in the "Keuangan & Pajak" section.
  SETTINGS_MONTHLY_STATEMENT: "/settings/monthly-statement",
  // Per-type admin notification toggles + package-alert thresholds.
  SETTINGS_NOTIFICATIONS: "/settings/notifications",
  // Reward-system configs. Open to any Admin since 2026-08-20 (Manager-only at
  // launch, server gate lifted to match).
  SETTINGS_LOYALTY: "/settings/loyalty",
  SETTINGS_REFERRAL: "/settings/referral",
  // Kode Diskon — studio-issued discount codes applied at purchase.
  SETTINGS_DISCOUNT_CODES: "/settings/discount-codes",
  // Instructor working hours / leave / class-type assignment enforcement,
  // off by default. Just the toggle — actual per-instructor management lives
  // on the instructor's own profile (InstructorDetails.tsx's "Manajemen" tab)
  // and the Staff list surfaces the at-a-glance status overview.
  SETTINGS_EMPLOYEE_MANAGEMENT: "/settings/employee-management",
  // Manager-only: which capabilities plain Admins lose (Pembatasan Admin).
  SETTINGS_ADMIN_RESTRICTIONS: "/settings/admin-restrictions",
  TIMEOFF: "/timeoff",
  CLASS: "/class",
  // The one Laporan index: every report is a row on this page. The seven
  // per-section routes below are kept only as redirects to it, so old
  // bookmarks still land somewhere sensible.
  ANALYTICS: "/analytics",
  ANALYTICS_CLASS: "/analytics/class",
  ANALYTICS_PACKAGE: "/analytics/package",
  ANALYTICS_MEMBERSHIP: "/analytics/membership",
  ANALYTICS_CLASS_PERFORMANCE: "/analytics/class/performance",
  ANALYTICS_PACKAGE_OVERVIEW: "/analytics/package/overview",
  ANALYTICS_PACKAGE_COMPARE: "/analytics/package/compare",
  // Combined status report: expiring soon / low credits / expired behind one
  // in-page view switcher (?view=). The membership twin has no low-credits
  // view (a membership holds no credit pool).
  ANALYTICS_PACKAGE_EXPIRY: "/analytics/package/expiry",
  ANALYTICS_PACKAGE_PURCHASES: "/analytics/package/purchases",
  ANALYTICS_MEMBERSHIP_OVERVIEW: "/analytics/membership/overview",
  ANALYTICS_MEMBERSHIP_COMPARE: "/analytics/membership/compare",
  ANALYTICS_MEMBERSHIP_EXPIRY: "/analytics/membership/expiry",
  ANALYTICS_MEMBERSHIP_PURCHASES: "/analytics/membership/purchases",
  // All six sections now have real reports. Visible to both Admin and
  // Manager; gating (where needed) applies per report/field, not at the
  // tab level.
  ANALYTICS_SALES: "/analytics/sales",
  ANALYTICS_SALES_SERVICES: "/analytics/sales/services",
  ANALYTICS_SALES_TREND: "/analytics/sales/trend",
  ANALYTICS_SALES_CLIENTS: "/analytics/sales/clients",
  ANALYTICS_SALES_PACKAGES: "/analytics/sales/packages",
  ANALYTICS_SALES_MEMBERSHIPS: "/analytics/sales/memberships",
  ANALYTICS_SALES_PACKAGES_PROMO: "/analytics/sales/packages-promo",
  ANALYTICS_SALES_MEMBERSHIPS_PROMO: "/analytics/sales/memberships-promo",
  ANALYTICS_CLIENT: "/analytics/client",
  ANALYTICS_CLIENT_PERFORMANCE: "/analytics/client/performance",
  ANALYTICS_CLIENT_INACTIVE: "/analytics/client/inactive",
  ANALYTICS_INSTRUCTOR: "/analytics/instructor",
  ANALYTICS_INSTRUCTOR_PERFORMANCE: "/analytics/instructor/performance",
  ANALYTICS_REWARD: "/analytics/reward",
  ANALYTICS_REWARD_LOYALTY: "/analytics/reward/loyalty",
  ANALYTICS_REWARD_REFERRAL: "/analytics/reward/referral",
  ANALYTICS_REWARD_USAGE: "/analytics/reward/usage",
  ANALYTICS_REWARD_DISCOUNT_CODES: "/analytics/reward/discount-codes",
  // System admin view
  BUSINESS: "/business",
  ADMIN: "/admin",
};

export default PATHS;
